import { z } from 'zod';
import { buyerCostConstantKindSchema, type BuyerCostConstantKind } from './buyer-cost-constant.js';
import { conditionBandSchema } from './asset.js';
import { calculationBasisSchema } from './financials.js';
import { skuGrainSchema } from './sku.js';

/**
 * SIL-1072: purchase_analysis / purchase_analysis_cost / purchase_analysis_line
 * wire DTOs (migration 896, docs/superpowers/specs/2026-09-16-market-
 * intelligence-design.md section 3 "DTOs live in packages/shared/src/dto/
 * purchase-analysis.ts").
 *
 * Percent-kind cost values and target_margin_pct are FRACTIONS (0.15 = 15%),
 * the same convention as buyer_cost_constant.
 */

export const purchaseLineTypeSchema = z.enum(['sku', 'asset_class']);
export type PurchaseLineType = z.infer<typeof purchaseLineTypeSchema>;

const PERCENT_KINDS = new Set<BuyerCostConstantKind>(['pct_of_revenue', 'pct_of_acquisition_cost']);

// ---------------------------------------------------------------------------
// purchase_analysis
// ---------------------------------------------------------------------------

export const purchaseAnalysisRowSchema = z.object({
  id: z.string().uuid(),
  customerOrgId: z.string().uuid(),
  name: z.string(),
  targetMarginPct: z.number(),
  costsSnapshotAt: z.string().nullable(),
  createdBy: z.string().uuid().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type PurchaseAnalysisRow = z.infer<typeof purchaseAnalysisRowSchema>;

/** Body for `POST /api/client/purchase-analyses`. */
export const purchaseAnalysisCreateSchema = z.object({
  name: z.string().min(1).max(200),
  targetMarginPct: z.number().min(0).max(1).default(0.2),
});
export type PurchaseAnalysisCreate = z.infer<typeof purchaseAnalysisCreateSchema>;

/** Body for `PATCH /api/client/purchase-analyses/:id`. */
export const purchaseAnalysisPatchSchema = z.object({
  name: z.string().min(1).max(200).optional(),
  targetMarginPct: z.number().min(0).max(1).optional(),
});
export type PurchaseAnalysisPatch = z.infer<typeof purchaseAnalysisPatchSchema>;

export const purchaseAnalysisListResponseSchema = z.object({
  rows: z.array(purchaseAnalysisRowSchema),
});
export type PurchaseAnalysisListResponse = z.infer<typeof purchaseAnalysisListResponseSchema>;

// ---------------------------------------------------------------------------
// purchase_analysis_cost
// ---------------------------------------------------------------------------

export const purchaseAnalysisCostRowSchema = z.object({
  analysisId: z.string().uuid(),
  customerOrgId: z.string().uuid(),
  key: z.string(),
  label: z.string(),
  kind: buyerCostConstantKindSchema,
  value: z.number(),
  overriddenAt: z.string().nullable(),
  costsSnapshotAt: z.string(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type PurchaseAnalysisCostRow = z.infer<typeof purchaseAnalysisCostRowSchema>;

/** One row of the `PUT /:id/costs` whole-replace body. */
export const purchaseAnalysisCostWriteRowSchema = z
  .object({
    key: z.string().min(1).max(100),
    label: z.string().min(1).max(200),
    kind: buyerCostConstantKindSchema,
    value: z.number().finite().min(0),
  })
  .refine((row) => !PERCENT_KINDS.has(row.kind) || row.value <= 1, {
    message: 'Percent-kind constants (pct_of_revenue, pct_of_acquisition_cost) must be <= 1 (a fraction, e.g. 0.15 for 15%).',
    path: ['value'],
  });
export type PurchaseAnalysisCostWriteRow = z.infer<typeof purchaseAnalysisCostWriteRowSchema>;

export const purchaseAnalysisCostWriteRequestSchema = z.object({
  rows: z.array(purchaseAnalysisCostWriteRowSchema),
});
export type PurchaseAnalysisCostWriteRequest = z.infer<typeof purchaseAnalysisCostWriteRequestSchema>;

export const purchaseAnalysisCostListResponseSchema = z.object({
  rows: z.array(purchaseAnalysisCostRowSchema),
  costsConfigured: z.boolean(),
});
export type PurchaseAnalysisCostListResponse = z.infer<typeof purchaseAnalysisCostListResponseSchema>;

// ---------------------------------------------------------------------------
// purchase_analysis_line
// ---------------------------------------------------------------------------

export const purchaseAnalysisLineRowSchema = z.object({
  id: z.string().uuid(),
  analysisId: z.string().uuid(),
  customerOrgId: z.string().uuid(),
  lineType: purchaseLineTypeSchema,
  skuId: z.string().uuid().nullable(),
  assetClassId: z.string().nullable(),
  sourceListingId: z.string().uuid().nullable(),
  quantity: z.number().int().positive(),
  conditionBand: conditionBandSchema,
  unitAcquisitionCostUsd: z.number(),
  createdAt: z.string(),
  updatedAt: z.string(),
  // SIL-1103 follow-up — display fields so the builder never has to show a
  // raw sku_id/asset_class_id. Sourced exactly the way GET /api/client/
  // model-suggest sources them (v_sku_catalog), so the k-anonymity floor is
  // identical: a suppressed FMV value is null here, never 0. Null for an
  // asset_class line (manufacturerName/modelName/grain) or a sku line whose
  // sku_id no longer resolves (assetClassName), never a raw id.
  manufacturerName: z.string().nullable(),
  modelName: z.string().nullable(),
  assetClassName: z.string().nullable(),
  grain: skuGrainSchema.nullable(),
  fmvBaselineUsd: z.number().nullable(),
  fmvRangeLowUsd: z.number().nullable(),
  fmvRangeHighUsd: z.number().nullable(),
  fmvSampleSize: z.number().int().nullable(),
});
export type PurchaseAnalysisLineRow = z.infer<typeof purchaseAnalysisLineRowSchema>;

const purchaseAnalysisLineWriteBaseSchema = z.object({
  lineType: purchaseLineTypeSchema,
  skuId: z.string().uuid().nullable().optional(),
  assetClassId: z.string().nullable().optional(),
  sourceListingId: z.string().uuid().nullable().optional(),
  quantity: z.number().int().positive().default(1),
  conditionBand: conditionBandSchema,
  unitAcquisitionCostUsd: z.number().finite().min(0).default(0),
});

/** Body for `POST /:id/lines` and `PATCH /:id/lines/:lineId` — line_type
 * must agree with which target column is set, mirroring the DB CHECK
 * `purchase_analysis_line_target_matches_type`. */
export const purchaseAnalysisLineWriteSchema = purchaseAnalysisLineWriteBaseSchema.refine(
  (v) =>
    (v.lineType === 'sku' && !!v.skuId && !v.assetClassId) ||
    (v.lineType === 'asset_class' && !!v.assetClassId && !v.skuId),
  {
    message: 'lineType must match exactly one of skuId (sku) or assetClassId (asset_class)',
    path: ['lineType'],
  },
);
export type PurchaseAnalysisLineWrite = z.infer<typeof purchaseAnalysisLineWriteSchema>;

export const purchaseAnalysisLinePatchSchema = purchaseAnalysisLineWriteBaseSchema.partial();
export type PurchaseAnalysisLinePatch = z.infer<typeof purchaseAnalysisLinePatchSchema>;

// ---------------------------------------------------------------------------
// POST /:id/lines/from-listing — SIL-1074: import SKU lines from a
// marketplace lot or listing. Lots resolve to SKU lines AT PICK TIME; the
// listing id is kept on each created line only as a "from lot X" provenance
// breadcrumb (sourceListingId) — there is no line_type = 'lot', and a later
// change to the lot's composition never alters lines already imported.
// ---------------------------------------------------------------------------

export const purchaseAnalysisImportFromListingRequestSchema = z.object({
  listingId: z.string().uuid(),
});
export type PurchaseAnalysisImportFromListingRequest = z.infer<
  typeof purchaseAnalysisImportFromListingRequestSchema
>;

export const purchaseAnalysisImportFromListingResponseSchema = z.object({
  lines: z.array(purchaseAnalysisLineRowSchema),
});
export type PurchaseAnalysisImportFromListingResponse = z.infer<
  typeof purchaseAnalysisImportFromListingResponseSchema
>;

// ---------------------------------------------------------------------------
// GET /:id — full detail (analysis + costs + lines)
// ---------------------------------------------------------------------------

export const purchaseAnalysisDetailResponseSchema = z.object({
  analysis: purchaseAnalysisRowSchema,
  costs: z.array(purchaseAnalysisCostRowSchema),
  costsConfigured: z.boolean(),
  lines: z.array(purchaseAnalysisLineRowSchema),
});
export type PurchaseAnalysisDetailResponse = z.infer<typeof purchaseAnalysisDetailResponseSchema>;

// ---------------------------------------------------------------------------
// POST /:id/compute — transient bid/cost overrides + computed result
// ---------------------------------------------------------------------------

/** Transient, unpersisted override for one line's bid, for the live-preview
 * debounced compute (spec §3 "POST /:id/compute"). Identifies the line by
 * id; a line with no override entry uses its saved unitAcquisitionCostUsd. */
export const purchaseComputeLineOverrideSchema = z.object({
  lineId: z.string().uuid(),
  unitAcquisitionCostUsd: z.number().finite().min(0).optional(),
});
export type PurchaseComputeLineOverride = z.infer<typeof purchaseComputeLineOverrideSchema>;

/** Transient, unpersisted override for one cost row's value. */
export const purchaseComputeCostOverrideSchema = z.object({
  key: z.string(),
  value: z.number().finite().min(0),
});
export type PurchaseComputeCostOverride = z.infer<typeof purchaseComputeCostOverrideSchema>;

export const purchaseComputeRequestSchema = z.object({
  lineOverrides: z.array(purchaseComputeLineOverrideSchema).default([]),
  costOverrides: z.array(purchaseComputeCostOverrideSchema).default([]),
  targetMarginPctOverride: z.number().min(0).max(1).optional(),
});
export type PurchaseComputeRequest = z.infer<typeof purchaseComputeRequestSchema>;

export const confidenceGradeSchema = z.enum(['A', 'B', 'C', 'D']);

export const purchaseMarginPointSchema = z.object({
  month: z.number().int(),
  revenueUsd: z.number(),
  perUnitCostUsd: z.number(),
  marginUsd: z.number(),
  marginPct: z.number().nullable(),
});
export type PurchaseMarginPointDto = z.infer<typeof purchaseMarginPointSchema>;

export const purchaseLineResultSchema = z.object({
  lineId: z.string().uuid(),
  forecastAvailable: z.boolean(),
  currentPriceSuppressed: z.boolean(),
  curve: z.array(purchaseMarginPointSchema),
  bestMonth: z.number().int(),
  sellNow: purchaseMarginPointSchema,
  atBestMonth: purchaseMarginPointSchema,
  bandAtBestMonth: z
    .object({ loUsd: z.number().nullable(), medUsd: z.number().nullable(), hiUsd: z.number().nullable() })
    .nullable(),
  breakEvenBidUsd: z.number().nullable(),
  bidAtTargetMarginUsd: z.number().nullable(),
  confidenceGrade: confidenceGradeSchema.nullable(),
});
export type PurchaseLineResultDto = z.infer<typeof purchaseLineResultSchema>;

/** Reproducibility stamping (spec §3 "Reproducibility stamping") — every
 * /compute response and the CSV carry these so a saved analysis can be
 * reopened with a signal, not a silently different answer. */
export const purchaseComputeStampSchema = z.object({
  formulaId: z.string(),
  forecastRunId: z.string().uuid().nullable(),
  forecastAsOf: z.string().nullable(),
  mcmpWindow: z.string().nullable(),
});
export type PurchaseComputeStamp = z.infer<typeof purchaseComputeStampSchema>;

export const purchaseComputeResponseSchema = z.object({
  lines: z.array(purchaseLineResultSchema),
  totalBidUsd: z.number(),
  totalRevenueUsd: z.number(),
  totalMarginUsd: z.number(),
  blendedMarginPct: z.number().nullable(),
  worstConfidenceGrade: confidenceGradeSchema.nullable(),
  linesWithoutForecastCount: z.number().int(),
  linesWithSuppressedPriceCount: z.number().int(),
  costsConfigured: z.boolean(),
  stamp: purchaseComputeStampSchema,
  /** Feeds the existing CalculationBasisDrawer (spec §2 "describePurchaseBasis()
   * feeds the existing CalculationBasisDrawer"). Analysis-level, not per line —
   * discloses the argmax convention, the band caveat, the k-anonymity floor,
   * and the price definition in use, once per response. */
  basis: calculationBasisSchema,
});
export type PurchaseComputeResponse = z.infer<typeof purchaseComputeResponseSchema>;
